import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableSortLabel,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  CardActions
} from '@mui/material';
import {
  Build as BuildIcon,
  Warning as WarningIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/authContext';
import { format, differenceInDays } from 'date-fns';
import { useOrganization } from '../../context/OrganizationContext';
import { exportMaintenanceLogsCSV } from '../../network/apis/maintenance.api';
import { useToast } from '../../hooks/useToast';
import styles from '../../styles/MaintenanceSchedule.module.css';

const MaintenanceSchedule = () => {
  const { currentOrganization } = useOrganization();
  const { token } = useAuth();
  const { showSuccess, showError } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, overdue, upcoming
  const [orderBy, setOrderBy] = useState('daysUntil'); // daysUntil, droneName, lastMaintenance
  const [order, setOrder] = useState('asc'); // asc, desc

  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year ago
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  useEffect(() => {
    if (currentOrganization?.organizationId) {
      fetchUpcomingMaintenance();
    }
  }, [currentOrganization]);

  const fetchUpcomingMaintenance = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/maintenance/upcoming`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch upcoming maintenance');
      }

      const data = await response.json();
      setDrones(data.drones || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    try {
      return differenceInDays(new Date(dateString), new Date());
    } catch {
      return null;
    }
  };

  const getStatusInfo = (nextMaintenanceDate) => {
    if (!nextMaintenanceDate) {
      return { label: 'Not Scheduled', color: 'default', severity: 0 };
    }

    const daysUntil = getDaysUntil(nextMaintenanceDate);

    if (daysUntil === null) {
      return { label: 'Invalid Date', color: 'default', severity: 0 };
    }

    if (daysUntil < 0) {
      return {
        label: `Overdue (${Math.abs(daysUntil)} days)`,
        color: 'error',
        severity: 4,
        daysUntil
      };
    } else if (daysUntil <= 7) {
      return {
        label: `Due in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
        color: 'warning',
        severity: 3,
        daysUntil
      };
    } else if (daysUntil <= 30) {
      return {
        label: `Due in ${daysUntil} days`,
        color: 'info',
        severity: 2,
        daysUntil
      };
    } else {
      return {
        label: `Due in ${daysUntil} days`,
        color: 'success',
        severity: 1,
        daysUntil
      };
    }
  };

  // Export handlers
  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);

      const params = {
        startDate: exportDateRange.startDate,
        endDate: exportDateRange.endDate
      };


      const { blob, filename } = await exportMaintenanceLogsCSV(token, currentOrganization.organizationUuid, params);


      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Maintenance logs exported successfully');
      setExportDialogOpen(false);
    } catch (error) {
      showError(error.message || 'Failed to export maintenance logs');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAndFilteredDrones = React.useMemo(() => {
    let filtered = [...drones];

    // Apply filter
    if (filter === 'overdue') {
      filtered = filtered.filter(drone => {
        if (!drone.nextMaintenanceDate) return false;
        const daysUntil = getDaysUntil(drone.nextMaintenanceDate);
        return daysUntil !== null && daysUntil < 0;
      });
    } else if (filter === 'upcoming') {
      filtered = filtered.filter(drone => {
        if (!drone.nextMaintenanceDate) return false;
        const daysUntil = getDaysUntil(drone.nextMaintenanceDate);
        // Include drones due within 0-30 days (not overdue, but upcoming)
        const isUpcoming = daysUntil !== null && daysUntil >= 0 && daysUntil <= 30;
        return isUpcoming;
      });
    }

    // Apply sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (orderBy === 'droneName') {
        aValue = `${a.manufacturer} ${a.model} ${a.nickname || ''}`.toLowerCase();
        bValue = `${b.manufacturer} ${b.model} ${b.nickname || ''}`.toLowerCase();
      } else if (orderBy === 'lastMaintenance') {
        aValue = a.lastMaintenanceDate ? new Date(a.lastMaintenanceDate).getTime() : 0;
        bValue = b.lastMaintenanceDate ? new Date(b.lastMaintenanceDate).getTime() : 0;
      } else if (orderBy === 'daysUntil') {
        const aStatus = getStatusInfo(a.nextMaintenanceDate);
        const bStatus = getStatusInfo(b.nextMaintenanceDate);
        // Sort by severity first (overdue first), then by days
        if (aStatus.severity !== bStatus.severity) {
          return order === 'asc'
            ? bStatus.severity - aStatus.severity
            : aStatus.severity - bStatus.severity;
        }
        aValue = aStatus.daysUntil ?? Infinity;
        bValue = bStatus.daysUntil ?? Infinity;
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [drones, filter, orderBy, order]);

  const stats = React.useMemo(() => {
    let overdue = 0;
    let dueSoon = 0; // within 7 days
    let upcoming = 0; // 8-30 days
    let notScheduled = 0;

    drones.forEach(drone => {
      if (!drone.nextMaintenanceDate) {
        notScheduled++;
        return;
      }

      const daysUntil = getDaysUntil(drone.nextMaintenanceDate);
      if (daysUntil === null) {
        notScheduled++;
      } else if (daysUntil < 0) {
        overdue++;
      } else if (daysUntil <= 7) {
        dueSoon++;
      } else if (daysUntil <= 30) {
        upcoming++;
      }
    });

    return { overdue, dueSoon, upcoming, notScheduled, total: drones.length };
  }, [drones]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Maintenance Schedule
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Fleet-wide maintenance overview for {currentOrganization?.name || 'your organization'}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Total
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'error.light' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Overdue
              </Typography>
              <Typography variant="h3" color="error.dark" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {stats.overdue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'warning.light' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                {isMobile ? 'Due 7 Days' : 'Due Within 7 Days'}
              </Typography>
              <Typography variant="h3" color="warning.dark" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {stats.dueSoon}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'info.light' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                {isMobile ? 'Due 30 Days' : 'Due Within 30 Days'}
              </Typography>
              <Typography variant="h3" color="info.dark" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {stats.upcoming}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Not Scheduled
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {stats.notScheduled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{
        mb: 2,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2,
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 200 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">All Drones ({drones.length})</MenuItem>
            <MenuItem value="overdue">Overdue Only ({stats.overdue})</MenuItem>
            <MenuItem value="upcoming">Due Within 30 Days ({stats.upcoming + stats.dueSoon})</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleOpenExportDialog}
          disabled={loading}
          fullWidth={isMobile}
        >
          Export CSV
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table/Cards */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          {sortedAndFilteredDrones.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {filter === 'all'
                    ? 'No drones found in your fleet.'
                    : filter === 'overdue'
                    ? 'No overdue maintenance. Great job!'
                    : 'No upcoming maintenance scheduled.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {sortedAndFilteredDrones.map((drone) => {
                const statusInfo = getStatusInfo(drone.nextMaintenanceDate);
                return (
                  <Card key={drone.id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      {/* Compact Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {drone.nickname || drone.model}
                          </Typography>
                          {drone.nickname && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {drone.model}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          icon={statusInfo.severity === 4 ? <WarningIcon /> : undefined}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>

                      {/* Compact Details - 2 columns */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            Last Maint.
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {formatDate(drone.lastMaintenanceDate)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            Next Due
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {formatDate(drone.nextMaintenanceDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    {/* Actions */}
                    <CardActions sx={{ p: 1, pt: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          window.location.href = `/fleet/drones`;
                        }}
                        sx={{ padding: '4px' }}
                      >
                        <BuildIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'droneName'}
                    direction={orderBy === 'droneName' ? order : 'asc'}
                    onClick={() => handleSort('droneName')}
                  >
                    Nickname / Model
                  </TableSortLabel>
                </TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'lastMaintenance'}
                    direction={orderBy === 'lastMaintenance' ? order : 'asc'}
                    onClick={() => handleSort('lastMaintenance')}
                  >
                    Last Maintenance
                  </TableSortLabel>
                </TableCell>
                <TableCell>Next Maintenance Due</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'daysUntil'}
                    direction={orderBy === 'daysUntil' ? order : 'asc'}
                    onClick={() => handleSort('daysUntil')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredDrones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      {filter === 'all'
                        ? 'No drones found in your fleet.'
                        : filter === 'overdue'
                        ? 'No overdue maintenance. Great job!'
                        : 'No upcoming maintenance scheduled.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedAndFilteredDrones.map((drone) => {
                  const statusInfo = getStatusInfo(drone.nextMaintenanceDate);
                  return (
                    <TableRow key={drone.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {drone.nickname || drone.model}
                          </Typography>
                          {drone.nickname && (
                            <Typography variant="caption" color="text.secondary">
                              {drone.model}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{drone.manufacturer}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {drone.serialNumber || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(drone.lastMaintenanceDate)}</TableCell>
                      <TableCell>{formatDate(drone.nextMaintenanceDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          icon={statusInfo.severity === 4 ? <WarningIcon /> : undefined}
                          sx={{ textTransform: 'capitalize', borderRadius: '4px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Maintenance History">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              // TODO: Navigate to drone maintenance history or open dialog
                              window.location.href = `/fleet/drones`;
                            }}
                          >
                            <BuildIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {sortedAndFilteredDrones.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedAndFilteredDrones.length} of {drones.length} drones
          </Typography>
        </Box>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Export Maintenance Logs to CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Select a date range to export maintenance logs for all drones in your organization. The export will include cost totals for accounting purposes.
            </Typography>

            <TextField
              label="Start Date"
              type="date"
              value={exportDateRange.startDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="End Date"
              type="date"
              value={exportDateRange.endDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <Alert severity="info">
              Export will include all maintenance logs with detailed cost breakdown and totals.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={exportLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exportLoading || !exportDateRange.startDate || !exportDateRange.endDate}
            startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {exportLoading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MaintenanceSchedule;
